import type {
  WorkspacesPageAssignableRolesQuery,
  WorkspacesPageDirectoryQuery,
  WorkspacesPageListQuery,
  WorkspacesPageMemberQuery,
  WorkspacesPageSharedAssignmentsQuery,
} from '../../../generated';
import type {
  AssignableLocation,
  DirectoryMember,
  MemberCount,
  Workspace,
} from '../types';

type ListResult = Extract<
  WorkspacesPageListQuery['workspaceList'],
  { __typename?: 'WorkspaceListResult' }
>;

type SharedResult = Extract<
  WorkspacesPageSharedAssignmentsQuery['teamMembersInSharedLocationsList'],
  { __typename?: 'TeamMembersInSharedLocationsListResult' }
>;

type DirectoryResult = Extract<
  WorkspacesPageDirectoryQuery['teamDirectorySearch'],
  { __typename?: 'TeamDirectorySearchResult' }
>;

type AssignableResult = Extract<
  WorkspacesPageAssignableRolesQuery['teamAssignableLocationRolesList'],
  { __typename?: 'TeamAssignableLocationRolesListResult' }
>;

type MemberResult = Extract<
  WorkspacesPageMemberQuery['teamMemberGet'],
  { __typename?: 'TeamMemberGetResult' }
>;

export const mapToWorkspaces = (
  workspaces: ListResult['workspaces']
): Workspace[] =>
  workspaces.map((workspace) => ({
    id: workspace.id,
    name: workspace.name,
    roots: workspace.roots.map((root) => ({
      id: root.id,
      name: root.node?.name ?? null,
      hierarchyType: root.node?.hierarchyType ?? null,
    })),
  }));

/**
 * The count as either read returns it: the whole answer, or the floor the
 * service stopped counting at.
 */
export type ApproximateCount =
  | { __typename?: 'ExactCount'; count: number }
  | { __typename?: 'AtLeastCount'; count: number };

/**
 * Which kind of answer the count is decides how it reads, so the member type is
 * carried through rather than flattened to a number the caller could misread.
 */
export const mapToMemberCount = (totalCount: ApproximateCount): MemberCount =>
  totalCount.__typename === 'AtLeastCount'
    ? { kind: 'AtLeast', count: totalCount.count }
    : { kind: 'Exact', count: totalCount.count };

export const mapToDirectoryMembers = (
  entries: SharedResult['teamMembers']
): DirectoryMember[] =>
  entries.map((entry) => ({
    userId: entry.user.id,
    name: entry.user.node?.name ?? null,
    email: entry.user.node?.email ?? null,
    phone: entry.user.node?.phone ?? null,
    avatarUrl: entry.user.node?.profileImage?.smallSquare ?? null,
    assignments: entry.assignedLocationRoles.map((assignment) => ({
      locationId: assignment.location.id,
      locationName: assignment.location.node?.name ?? null,
      hierarchyType: assignment.location.node?.hierarchyType ?? null,
      parentName: assignment.location.node?.parent?.node?.name ?? null,
      role: {
        id: assignment.teamRole.id,
        displayName: assignment.teamRole.displayName,
        description: assignment.teamRole.description,
        locationType: assignment.teamRole.locationType,
      },
    })),
  }));

/**
 * The members the directory read matched. The directory lists the whole
 * workspace rather than only what the caller shares, so it is asked for names
 * alone and the assignments come back empty: the roles behind a member are read
 * one named user at a time, through mapToMember.
 */
export const mapToDirectoryEntries = (
  entries: DirectoryResult['entries']
): DirectoryMember[] =>
  entries.map((entry) => ({
    userId: entry.user.id,
    name: entry.user.node?.name ?? null,
    email: entry.user.node?.email ?? null,
    phone: entry.user.node?.phone ?? null,
    avatarUrl: entry.user.node?.profileImage?.smallSquare ?? null,
    assignments: [],
  }));

/**
 * One member, as the point read answers it. The read returns the whole member
 * rather than a list entry, so the id comes along; the panel renders the same
 * flattened shape the table does.
 */
export const mapToMember = (
  teamMember: MemberResult['teamMember']
): DirectoryMember => ({
  userId: teamMember.user.id,
  name: teamMember.user.node?.name ?? null,
  email: teamMember.user.node?.email ?? null,
  phone: teamMember.user.node?.phone ?? null,
  avatarUrl: teamMember.user.node?.profileImage?.smallSquare ?? null,
  assignments: teamMember.assignedLocationRoles.map((assignment) => ({
    locationId: assignment.location.id,
    locationName: assignment.location.node?.name ?? null,
    hierarchyType: assignment.location.node?.hierarchyType ?? null,
    parentName: assignment.location.node?.parent?.node?.name ?? null,
    role: {
      id: assignment.teamRole.id,
      displayName: assignment.teamRole.displayName,
      description: assignment.teamRole.description,
      locationType: assignment.teamRole.locationType,
    },
  })),
});

export const mapToAssignableLocations = (
  assignable: AssignableResult['assignableLocationRoles']
): AssignableLocation[] =>
  assignable.map((entry) => ({
    locationId: entry.location.id,
    locationName: entry.location.node?.name ?? null,
    hierarchyType: entry.location.node?.hierarchyType ?? null,
    parentName: entry.location.node?.parent?.node?.name ?? null,
    roles: entry.roles.map((role) => ({
      id: role.id,
      displayName: role.displayName,
      description: role.description,
      locationType: role.locationType,
    })),
  }));

/**
 * A member whose projection carries no name is still a real membership. The
 * row is named in words and the id is shown beside it, rather than passing a
 * raw id off as someone's name.
 */
export const memberName = (name: string | null): string =>
  name ?? 'Unnamed user';

/**
 * A location the supplier service could not resolve still has an id worth
 * showing — the membership is real even when the ref is stale.
 */
export const locationLabel = (name: string | null, id: string): string =>
  name ?? id;

/** Initials for the avatar fallback, so an unnamed row still reads as a person. */
export const memberInitials = (name: string | null): string =>
  (name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

/**
 * How deep a location sits, read from its tier.
 *
 * The read returns the tree already in depth-first order but without parent
 * pointers, and the three tiers always nest in the same order, so the tier is
 * the depth. A location whose tier is unknown is treated as a leaf rather than
 * given a level it may not have.
 */
export const locationDepth = (hierarchyType: string | null): number => {
  switch (hierarchyType) {
    case 'Organization':
      return 0;
    case 'Distributor':
      return 1;
    default:
      return 2;
  }
};

export interface TierStyle {
  /** Row tint. The only thing that carries the tier's colour. */
  background: string;
  /** The same colour as a dot, for places too small to tint. */
  dot: string;
  label: string;
}

/**
 * How a tier is shown. Only the background changes colour — text and chips
 * stay neutral, so the nesting reads at a glance without the rows shouting.
 */
export const tierStyle = (hierarchyType: string | null): TierStyle => {
  switch (hierarchyType) {
    case 'Organization':
      return {
        background: 'bg-primary/10',
        dot: 'bg-primary',
        label: 'Organization',
      };
    case 'Distributor':
      return {
        background: 'bg-secondary/10',
        dot: 'bg-secondary',
        label: 'Distributor',
      };
    case 'Warehouse':
      return {
        background: 'bg-warning/10',
        dot: 'bg-warning',
        label: 'Warehouse',
      };
    default:
      return {
        background: 'bg-default-100',
        dot: 'bg-default-400',
        label: 'Location',
      };
  }
};
